import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import type { FormsAndFormatsSubMode } from "@/components/proposal-formats/formsAndFormatsSubModes";
import { setPendingServiceSetupOffering } from "@/lib/service/pendingServiceSetupOpen";

export const WORKSPACE_MODE_REQUEST_EVENT = "bb-workspace-mode-request";

const FORMS_FORMATS_SUB_MODE_KEY = "bb-forms-formats-pending-submode";
const PENDING_SIGN_TEMPLATE_VERSION_KEY = "bb-pending-sign-template-version";
const PENDING_PROPOSAL_PRODUCT_TYPE_KEY = "bb-pending-proposal-product-type";
const PENDING_MFG_PART_USE_KEY = "bb-pending-mfg-part-use";

export type WorkspaceModeRequestDetail = {
  mode: PrimaryMode;
  formsFormatsSubMode?: FormsAndFormatsSubMode;
  serviceSetupOfferingId?: string;
  serviceSetupOfferingSlug?: string;
};

export function requestWorkspaceMode(detail: WorkspaceModeRequestDetail): void {
  if (typeof window === "undefined") return;
  if (detail.formsFormatsSubMode) {
    try {
      sessionStorage.setItem(FORMS_FORMATS_SUB_MODE_KEY, detail.formsFormatsSubMode);
    } catch {
      // ignore
    }
  }
  if (detail.serviceSetupOfferingId || detail.serviceSetupOfferingSlug) {
    setPendingServiceSetupOffering({
      offeringId: detail.serviceSetupOfferingId,
      offeringSlug: detail.serviceSetupOfferingSlug,
    });
  }
  window.dispatchEvent(
    new CustomEvent<WorkspaceModeRequestDetail>(WORKSPACE_MODE_REQUEST_EVENT, { detail })
  );
}

export function openSignTemplateEditor(versionId: string): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(PENDING_SIGN_TEMPLATE_VERSION_KEY, versionId);
  } catch {
    // ignore
  }
  requestWorkspaceMode({ mode: "formsAndFormats", formsFormatsSubMode: "signTemplates" });
}

export function consumePendingSignTemplateVersionId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const v = sessionStorage.getItem(PENDING_SIGN_TEMPLATE_VERSION_KEY);
    sessionStorage.removeItem(PENDING_SIGN_TEMPLATE_VERSION_KEY);
    if (v && /^[0-9a-f-]{36}$/i.test(v)) return v;
  } catch {
    // ignore
  }
  return null;
}

/** Leave the current screen and open one part use in Models → Part uses. */
export function openManufacturingPartUse(useId: string): void {
  if (typeof window === "undefined") return;
  const id = useId.trim();
  if (!/^[0-9a-f-]{36}$/i.test(id)) return;
  try {
    sessionStorage.setItem(PENDING_MFG_PART_USE_KEY, id);
  } catch {
    // ignore
  }
  requestWorkspaceMode({ mode: "mfgModels" });
}

export function consumePendingManufacturingPartUseId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const id = sessionStorage.getItem(PENDING_MFG_PART_USE_KEY);
    sessionStorage.removeItem(PENDING_MFG_PART_USE_KEY);
    if (id && /^[0-9a-f-]{36}$/i.test(id)) return id;
  } catch {
    // ignore
  }
  return null;
}

export function openServiceBoard(): void {
  requestWorkspaceMode({ mode: "serviceBoard" });
}

export function openServiceSetup(args?: { offeringId?: string; offeringSlug?: string }): void {
  requestWorkspaceMode({
    mode: "serviceDefinitions",
    serviceSetupOfferingId: args?.offeringId,
    serviceSetupOfferingSlug: args?.offeringSlug,
  });
}

export function openManagerProposalCopy(productTypeSlug: string): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(PENDING_PROPOSAL_PRODUCT_TYPE_KEY, productTypeSlug.trim());
  } catch {
    // ignore
  }
  requestWorkspaceMode({ mode: "formsAndFormats", formsFormatsSubMode: "proposals" });
}

export function consumePendingProposalProductType(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const slug = sessionStorage.getItem(PENDING_PROPOSAL_PRODUCT_TYPE_KEY);
    sessionStorage.removeItem(PENDING_PROPOSAL_PRODUCT_TYPE_KEY);
    return slug?.trim() || null;
  } catch {
    return null;
  }
}

export function consumePendingFormsFormatsSubMode(): FormsAndFormatsSubMode | null {
  if (typeof window === "undefined") return null;
  try {
    const v = sessionStorage.getItem(FORMS_FORMATS_SUB_MODE_KEY);
    sessionStorage.removeItem(FORMS_FORMATS_SUB_MODE_KEY);
    if (v === "documents" || v === "proposals" || v === "signTemplates" || v === "assetLibrary") return v;
    if (v === "forms") return "documents";
  } catch {
    // ignore
  }
  return null;
}
